import { ReactNode, useEffect, useState } from 'react';

import FilesTab from '@atom/components/common/filesTab/FilesTab';
import textConstants from '@atom/constants/textConstants';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Icon, Progress, Snackbar } from '@atom/mui';
import { ErrorOutlineIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetDetailView } from '@atom/types/asset';
import { SubjectType } from '@atom/types/media';
import { PolicyAction } from '@atom/types/policy';
import { hasAccess } from '@atom/utilities/accessUtilities';

import CostTab from './costTab/CostTab';
import InventoryDetailHeader from './header/InventoryDetailHeader';
import InfoTab from './infoTab/InfoTab';
import PhotosTab from './photosTab/PhotosTab';
import RequestsTab from './requestsTab/RequestsTab';
import SubItemsTab from './subItemsTab/SubItemsTab';
import WorkOrdersTab from './workOrdersTab/WorkOrdersTab';
import InventoryDetailContext, {
  ConversionUnitSelection,
} from './InventoryDetailContext';
import useInventoryAssetDetail from './useInventoryAssetDetail';

import classes from './detail.module.css';

const styles = {
  errorIcon: {
    fontSize: '3rem',
  },
};

const InventoryDetail = () => {
  const [searchParamsObject, { params, updateSearchParams }] = useNavigation();
  const { id } = params;

  const {
    asset,
    dispatch,
    loading,
    error,
    refetch,
    updateAsset,
    updateAttributes,
    deleteAsset,
    approveAllChanges,
    approveAttributeChange,
    rejectAttributeChange,
    subItemTree,
    subItemTreeDispatch,
    fetchAssetDetail,
    loadNode,
    refetchNodes,
    refreshChangeTypes,
    refreshSubItemTree,
    conversionUnits,
  } = useInventoryAssetDetail(id);

  const [inventoryMoveDisabled, setInventoryMoveDisabled] =
    useState<boolean>(false);
  const [conversionUnitSelection, setConversionUnitSelection] =
    useState<ConversionUnitSelection>();

  useEffect(() => {
    if (!searchParamsObject.activeView) {
      updateSearchParams({ activeView: AssetDetailView.ASSET_INFO });
    }
  }, []);

  useEffect(() => () => Snackbar.dismiss(), []);

  useEffect(() => {
    if (error && asset.id === id) {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  }, [error]);

  const activeView: AssetDetailView =
    (searchParamsObject.activeView as AssetDetailView) ||
    AssetDetailView.ASSET_INFO;

  const getBodyPane = (view: AssetDetailView): ReactNode => {
    const canCreateMedia = hasAccess(PolicyAction.CREATE_MEDIA, asset.actions);
    const canUpdateMedia = hasAccess(PolicyAction.UPDATE_MEDIA, asset.actions);
    const canDeleteMedia = hasAccess(PolicyAction.DELETE_MEDIA, asset.actions);

    const content: Record<AssetDetailView, ReactNode> = {
      [AssetDetailView.ASSET_INFO]: <InfoTab />,
      [AssetDetailView.SUB_ITEMS]: <SubItemsTab />,
      [AssetDetailView.WORK]: <WorkOrdersTab />,
      [AssetDetailView.FILES]: (
        <FilesTab
          subjectId={asset.id}
          subjectType={SubjectType.INVENTORY_ASSET}
          canCreate={canCreateMedia}
          canUpdate={canUpdateMedia}
          canDelete={canDeleteMedia}
          onSelectedChange={(selected: string[]) =>
            setInventoryMoveDisabled(Boolean(selected.length))
          }
        />
      ),
      [AssetDetailView.PHOTOS]: <PhotosTab />,
      [AssetDetailView.COST]: <CostTab />,
      [AssetDetailView.REQUESTS]: <RequestsTab />,
    };

    return <div className={classes.bodyContainer}>{content[view]}</div>;
  };

  if (error && asset.id !== id) {
    return (
      <div className={classes.errorContainer} data-cy="inventoryDetailError">
        <Icon
          style={styles.errorIcon}
          color={colors.neutral.gray}
          icon={ErrorOutlineIcon}
        />
        <div className={classes.errorTitle}>Unable to load this asset</div>
        <div className={classes.errorSubtext}>
          {textConstants.GENERIC_APPLICATION_ERROR_TEXT}
        </div>
        <Button
          variant="contained"
          color="primary"
          data-cy="inventoryDetailErrorRetryButton"
          onClick={() => refetch().catch(() => undefined)}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (asset.id !== id) {
    return (
      <div className={classes.spinnerContainer}>
        <Progress />
      </div>
    );
  }

  return (
    <InventoryDetailContext.Provider
      value={{
        asset,
        dispatch,
        loading,
        error,
        refetch,
        updateAsset,
        updateAttributes,
        deleteAsset,
        approveAllChanges,
        approveAttributeChange,
        rejectAttributeChange,
        subItemTree,
        subItemTreeDispatch,
        fetchAssetDetail,
        loadNode,
        refetchNodes,
        refreshChangeTypes,
        refreshSubItemTree,
        conversionUnits,
        conversionUnitSelection,
        setConversionUnitSelection,
      }}
    >
      <div className={classes.inventoryDetailContainer}>
        <InventoryDetailHeader inventoryMoveDisabled={inventoryMoveDisabled} />
        {getBodyPane(activeView)}
      </div>
    </InventoryDetailContext.Provider>
  );
};

export default InventoryDetail;
